import { confirmable } from 'react-confirm';
import { toast } from 'react-toastify';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { Button, DropZone, Icon, IndexTable } from '@shopify/polaris';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { RHFAPIAutocompleteV2 } from '../hook_form/rhf_api_autocomplete_v2';
import FormProvider from '../hook_form/form_provider';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'src/utils/type_check';
import FixedTable from '../table/fixed_table';
import useTable from 'src/hooks/use_table';
import { AlertTriangleIcon, CheckIcon } from '@shopify/polaris-icons';
import { utils, writeFile } from 'xlsx';
import { getInboundExcelSample, importInboundExcel, useGetInbounds } from 'src/api/inbound_api';
import { useGetLocations } from 'src/api/location_api';
import { useGetFeatureSettingReference } from 'src/api/reference_api';
import { getErrorMessage } from 'src/utils/error_message';
import { fNumber } from 'src/utils/format_number';
import { requiredField } from 'src/utils/format_data';
import { chooseLan } from 'src/utils/language_code';
import { fDateTime } from 'src/utils/format_time';
import { Badge } from '../badge/badge';
import { rangeObj } from '../date_picker/dates';
import { LogInAlert } from '../alert/alert';

const alertStyle = {
  paddingY: 0,
  alignItems: 'center',
  '& .MuiAlert-icon': { alignItems: 'center', paddingY: 0, color: 'inherit' },
  '& .MuiAlert-action': { alignItems: 'center', paddingTop: 0 },
};

const FAILED_TABLE_MAX_HEIGHT = 392;

const FAILURE_FILE_NAME = 'inbound_import_failures_';

const FEATURE_KEY = 'ExcelInboundPutaway';

const RECEIVABLE_STATUS = ['Draft', 'Receiving'];

const purchaseOrderSearchRange = rangeObj().past6months;

const ellipsisStyle = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const flexColumnStyle = { width: '40%' };

const quantityColumnStyle = { width: '20%', textAlign: 'left' };

const optionContentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--p-space-200)',
  flex: 1,
  minWidth: 0,
};

const checkIconSpaceStyle = { paddingRight: '26px' };

function InboundImportDialog({ show, proceed, locationId }) {
  const { t } = useTranslation(['inbound', 'excel', 'placeholder', 'common']);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [customerId, setCustomerId] = useState('');

  const methods = useForm({
    defaultValues: { locationId: locationId, purchaseOrderId: '' },
  });

  const { watch } = methods;

  const purchaseOrderId = watch('purchaseOrderId');
  const { searchResults: featureSetting } = useGetFeatureSettingReference({
    featureKey: isEmpty(customerId) ? '' : FEATURE_KEY,
    userId: customerId,
    locationId: locationId,
  });
  const isFeatureDisabled = featureSetting?.settingValue?.enabled === false;
  const errors = report?.errors ?? [];

  const runImport = useCallback(
    async (validateOnly, poId, excelFile) => {
      setWarningMessage('');
      setIsRunning(true);
      try {
        const res = await importInboundExcel({
          purchaseOrderId: poId,
          locationId: locationId,
          excelFile: excelFile,
          validateOnly: validateOnly,
        });
        if (!res.isSuccess) throw Error();
        if (!validateOnly) {
          toast.success(t('excel:import.executed', { total: res.result.validRows }));
          proceed(true);
          return;
        }
        setReport(res.result);
      } catch (error) {
        setWarningMessage(getErrorMessage(error));
      }
      setIsRunning(false);
    },
    [locationId, proceed, t],
  );

  const changeFile = (next) => {
    setFile(next);
    setReport(null);
    setWarningMessage('');
  };

  const handleDropZoneDrop = (_dropFiles, acceptedFiles) => {
    changeFile(acceptedFiles[0]);
  };

  const renderLocation = (
    <Box>
      <RHFAPIAutocompleteV2
        displayName={t('excel:import.location')}
        name="locationId"
        useOptions={useGetLocations}
        params={{ pageSize: 100, pageNumber: 1, locationId: locationId }}
        toOption={(r) => ({
          label: chooseLan(r.locationNameLocal, r.locationNameEnglish),
          value: r.locationId,
        })}
        searchParamName={'q'}
        disabled
        hideSearchIcon
      />
    </Box>
  );

  const renderPurchaseOrder = (
    <Box>
      <RHFAPIAutocompleteV2
        displayName={requiredField(t('inbound:field.po_no'))}
        name="purchaseOrderId"
        placeholder={t('placeholder:search_purchase_order')}
        useOptions={useGetInbounds}
        params={{
          pageSize: 20,
          pageNumber: 1,
          locationId: locationId,
          purchaseOrderStatus: RECEIVABLE_STATUS,
          fromDt: purchaseOrderSearchRange.fromDt,
          toDt: purchaseOrderSearchRange.toDt,
        }}
        toOption={(r) => ({
          label: r.wmsPurchaseOrderNumber,
          value: r.purchaseOrderId,
          userId: r.userId,
          customerName: chooseLan(r.userNameLocal, r.userNameEnglish),
          receivedQuantity: r.receivedQuantity,
          orderedQuantity: r.orderedQuantity,
        })}
        searchParamName={'q'}
        resetSearchOnOpen
        onChangeWithValue
        onChange={(option) => {
          setCustomerId(option?.userId ?? '');
          setReport(null);
        }}
        renderOptionContent={(option, selected) => (
          <Box sx={{ ...optionContentStyle, ...(!selected && checkIconSpaceStyle) }}>
            <Typography
              sx={{ ...flexColumnStyle, ...ellipsisStyle }}
              fontWeight={selected ? '650' : '400'}
            >
              {option.label}
            </Typography>
            <Typography sx={{ ...quantityColumnStyle, ...ellipsisStyle }}>
              {`${fNumber(option.receivedQuantity)}/${fNumber(option.orderedQuantity)}`}
            </Typography>
            <Typography sx={{ ...flexColumnStyle, ...ellipsisStyle }}>
              {option.customerName}
            </Typography>
          </Box>
        )}
      />
    </Box>
  );

  const dropFile =
    file === null ? (
      <DropZone.FileUpload
        actionTitle={t('excel:add')}
        actionHint={`${t('excel:accepts')} .xlsx`}
      />
    ) : (
      <Stack
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        direction={'row'}
        px={2}
        py={4}
      >
        <Typography variant="body_md_semibold">{file?.name}</Typography>
        <Button onClick={() => changeFile(null)}>{t('excel:replace')}</Button>
      </Stack>
    );

  const renderFeatureAlert = isFeatureDisabled && (
    <LogInAlert
      severity="error"
      sx={alertStyle}
      errorMsg={t('excel:import.feature_disabled')}
    />
  );

  const renderAlert = !isEmpty(warningMessage) && (
    <LogInAlert
      onClose={() => setWarningMessage('')}
      severity="error"
      sx={alertStyle}
      errorMsg={warningMessage}
    />
  );

  const resultMessage = () => {
    if (report.errorRows === 0) {
      return t('excel:import.ready', { total: report.totalRows });
    }
    return t('excel:import.validated_with_errors', {
      total: report.totalRows,
      fail: report.errorRows,
    });
  };

  const renderResultAlert = report ? (
    report.errorRows > 0 ? (
      <LogInAlert
        severity="error"
        sx={alertStyle}
        errorMsg={resultMessage()}
      />
    ) : (
      <Alert
        icon={<Icon source={CheckIcon} />}
        sx={alertStyle}
      >
        {resultMessage()}
      </Alert>
    )
  ) : null;

  const exportFailures = () => {
    const rows = errors.map((e) => ({
      [t('excel:row_number')]: e.rowNumber,
      [t('excel:import.column')]: isEmpty(e.column) ? '' : e.column,
      [t('excel:import.value')]: e.value,
      [t('excel:error_cause')]: e.message,
    }));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rows), 'errors');
    writeFile(wb, `${FAILURE_FILE_NAME}${fDateTime(new Date())}.xlsx`);
  };

  const renderFailedHeader = (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1}
      >
        <Typography
          variant={'body_md_semibold'}
          sx={{ color: 'var(--p-color-text-critical)' }}
        >
          {t('excel:import.failed')}
        </Typography>
        <Badge tone={'critical'}>{errors.length}</Badge>
      </Stack>
      <Button onClick={exportFailures}>{t('excel:import.export_failures')}</Button>
    </Stack>
  );

  const table = useTable({
    data: errors,
    headers: [
      { title: '' },
      { title: t('excel:row_number') },
      { title: t('excel:import.column') },
      { title: t('excel:import.value') },
      { title: t('excel:error_cause') },
    ],
    showPagination: false,
    showRowsPerPage: false,
    rowMarkup: ({ data }) => {
      return data.map(({ rowNumber, column, value, message }, index) => (
        <IndexTable.Row
          id={index}
          key={index}
          position={index}
          onClick={() => {}}
        >
          <Box
            component={IndexTable.Cell}
            sx={{ width: '40px' }}
          >
            <Icon
              source={AlertTriangleIcon}
              tone="critical"
            />
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '10%' }}
          >
            {rowNumber}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '20%' }}
          >
            {isEmpty(column) ? '' : column}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '25%' }}
          >
            {value}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '40%' }}
          >
            {message}
          </Box>
        </IndexTable.Row>
      ));
    },
  });

  const content = (
    <FormProvider methods={methods}>
      <Stack spacing={1.5}>
        {renderAlert}
        {renderFeatureAlert}
        {renderResultAlert}
        {renderLocation}
        {renderPurchaseOrder}
        <DropZone
          onDrop={handleDropZoneDrop}
          allowMultiple={false}
          variableHeight
          accept=".xlsx"
          disabled={isFeatureDisabled}
        >
          {dropFile}
        </DropZone>
        {errors.length > 0 && (
          <>
            {renderFailedHeader}
            <FixedTable
              useTable={table}
              maxHeight={FAILED_TABLE_MAX_HEIGHT}
            />
          </>
        )}
      </Stack>
    </FormProvider>
  );

  const primaryAction =
    report === null
      ? {
          content: t('excel:import.verify'),
          onAction: () => runImport(true, purchaseOrderId, file),
          loading: isRunning,
          disabled: isFeatureDisabled || isEmpty(purchaseOrderId) || file === null,
        }
      : {
          content: t('excel:import.upload'),
          onAction: () => runImport(false, purchaseOrderId, file),
          loading: isRunning,
          disabled: report.canExecute === false,
        };

  const footerLeftNode = (
    <Button
      variant="tertiary"
      onClick={async () => {
        await getInboundExcelSample();
      }}
    >
      <Typography
        variant={'body_md'}
        sx={{ color: 'link', textDecoration: 'underline' }}
      >
        {t('excel:download')}
      </Typography>
    </Button>
  );

  return (
    <StackedModal
      isOpen={show}
      title={t('common:button.import')}
      modalContentStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      content={content}
      primaryAction={primaryAction}
      footerLeftNode={footerLeftNode}
      onClose={() => {
        proceed(false);
      }}
    />
  );
}

export default confirmable(InboundImportDialog);
